'use client';
import {
  IMessagePayload,
  useMessageContext,
  useToast,
  ContextUserType,
  IMessagesQueryParams,
  IMessage,
} from '@ui/components';
import { useEffect, useState, useRef } from 'react';
import { useFiles, usePostHogFeatureFlag } from '@ui/hooks';
import { useCreatorVideoUploadPermission } from '@ui/hooks/use-creator-video-upload-permission';
import { sendGAEvent } from '@next/third-parties/google';
import dynamic from 'next/dynamic';
import { ContentType } from '@ui/constants/context';
import { useInteractionGuard } from '@hooks/index';
import { useReplyRefContext } from '@ui/contexts';

const RichTextEditor = dynamic(
  () => import('@ui/components/ui/rte-editor/rte-editor').then((mod) => mod.RichTextEditor),
  { ssr: false },
);
const maxFiles = 1;
const initialState: IMessagePayload = {
  send_email: false,
  files: {
    images: [],
  },
  description: '',
  miscellaneous: {},
};
interface Props {
  creator_id: string;
  user_id: string;
  contentType: ContentType;
  userType: ContextUserType;
  replyTo?: { id: string; label: string };
  activateToolbarOnFocus: boolean;
  createThreadIfNotExist: boolean;
  thread_id?: string;
  message_id?: string;
  conversation_id?: string;
  queryParam: IMessagesQueryParams;
  main_post_ref: string;
  miscellaneous?: IMessagePayload['miscellaneous'];
  community_details: IMessage['community_details'];
  onDismiss?: () => void;
  creatorData?: { platform_settings?: { enable_video_uploads?: boolean } };
}
export const CommentOrReplyForm = (props: Props) => {
  const { interceptInteraction } = useInteractionGuard();
  const toast = useToast();
  const [loadingType, setLoadingType] = useState<ContentType | null>(null);
  const messageState = useMessageContext(props.userType);
  const [message, setMessage] = useState<IMessagePayload>(initialState);
  const isVideoStreamUploadEnabled = usePostHogFeatureFlag('video-stream-upload');
  const { isVideoUploadEnabled } = useCreatorVideoUploadPermission(props.creatorData);
  const { files, onSelectFiles, deleteFile, onDiscardFiles, error } = useFiles({
    maxFiles,
    allowVideos: isVideoStreamUploadEnabled && isVideoUploadEnabled ? true : false,
  });
  const { setExpanded } = useReplyRefContext();
  const [displayError, setDisplayError] = useState<string | null>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setMessage((state) => ({ ...state, files: { images: files } }));
  }, [files]);

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (error) {
      setDisplayError(error);
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
      errorTimerRef.current = setTimeout(() => {
        setDisplayError(null);
      }, 5000);
    }

    return () => {
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
    };
  }, [error]);

  const onSubmit = async (
    description: string,
    mentions: string[],
    isSaveDraft: boolean,
    clearEditorContent: () => void,
  ) => {
    setLoadingType(props.contentType);
    try {
      let createdThread = null;
      if (
        props.createThreadIfNotExist &&
        !props.thread_id &&
        props.message_id &&
        props.conversation_id
      ) {
        // create comment thread
        const threadResponse = await messageState.onCreateMessageThread({
          message_id: props.message_id,
          conversation_id: props.conversation_id,
          queryParams: props.queryParam,
        });
        createdThread = threadResponse?.data?.message_thread?.id;
      }

      const creator_main_id =
        props.creator_id !== null ? props.creator_id : messageState.singleMessage?.creator_id;

      const res = await messageState.onCreateMessage({
        message: {
          ...message,
          description,
          creator_id: creator_main_id,
          thread_id: createdThread || props.thread_id,
          user_id: props.user_id,
          mentions,
          type: props.contentType,
          main_post_ref: props.main_post_ref,
          draft: isSaveDraft,
          miscellaneous: props.miscellaneous,
          community_details: props.community_details,
        },
      });

      if (props.message_id) {
        setExpanded(props.message_id, true);
      }

      if (!res?.error) {
        setMessage(initialState);
        onDiscardFiles();
        clearEditorContent();
        // Send GA Event for successful comment creation
        if (props.contentType === 'comment') {
          sendGAEvent('event', 'CreateCommentSuccessful', {
            event_label: 'Comment Creation Successful',
            creatorId: props.creator_id,
            postId: props.message_id,
          });
        }
        if (props.onDismiss) props.onDismiss();
      } else {
        toast(
          { title: `Error creating ${props.contentType}, please try again` },
          { type: 'error' },
        );
      }
    } catch (e) {
      console.error(e);
      toast({ title: `Error creating ${props.contentType}, please try again` }, { type: 'error' });

      // Send GA Event for failed comment creation
      if (props.contentType === 'comment') {
        sendGAEvent('event', 'CreateCommentFailed', {
          event_label: 'Comment Creation Failed',
          creatorId: props.creator_id,
          postId: props.message_id,
        });
      }
    } finally {
      setLoadingType(null);
    }
  };

  const onCancelEditor = () => {
    setMessage(initialState);
    onDiscardFiles();
    if (props.onDismiss) props.onDismiss();
  };

  const onClickCommentBox = interceptInteraction(() => {},
  'You are not a fan of this creator. Collect this badge to join.');

  return (
    <div className="flex flex-col gap-1">
      {messageState.isAuthenticated && messageState.isFanOfCreator ? (
        <>
          {displayError && (
            <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600 font-medium">{displayError}</p>
            </div>
          )}
          <RichTextEditor
            deleteFile={deleteFile}
            onSelectFiles={onSelectFiles}
            isSubmitButtonDisabled={false}
            onChange={onSubmit}
            value={message.description || ''}
            userType="creator"
            creatorDisplayName={messageState?.community_details?.creator_display_name}
            currentUser={props.userType}
            files={files}
            exceededFileLimit={files.length >= maxFiles}
            placeholder={`Type your ${props.contentType}...`}
            editorLabel=""
            buttonLabel={props.contentType === 'comment' ? 'Comment' : 'Reply'}
            editorOnly
            maxFiles={maxFiles}
            activateToolbarOnFocus={props.activateToolbarOnFocus}
            isLoading={loadingType === props.contentType}
            showCancelButton
            onCancel={onCancelEditor}
            enableSuggestions
            initialMention={props.replyTo || undefined}
            allowVideos={isVideoStreamUploadEnabled && isVideoUploadEnabled ? true : false}
          />
        </>
      ) : (
        <div className="bg-input h-fit rounded-md py-3 pl-4" onClick={onClickCommentBox}>
          <p className="text-sm text-gray-400">
            Type your {props.contentType === 'comment' ? 'comment' : 'reply'}...
          </p>
        </div>
      )}
    </div>
  );
};
